// Live, need-scoped practical updates. Authored words never enter navigation,
// logging or notification calls; every affordance comes from the boundary read.

import { Button, Card, EmptyState, StatusMessage, cx } from "@herefor/design-system";
import { PRACTICAL_UPDATE_MESSAGES as COPY } from "@herefor/product-language";
import { useMutation, useQuery } from "convex/react";
import { useState, type SyntheticEvent } from "react";

import {
  practicalUpdateDocumentId,
  practicalUpdateMutations,
  practicalUpdateNeedDocumentId,
  practicalUpdateQueries,
  type PracticalUpdateView,
} from "../../convex/practicalUpdates";
import { useConnectivity } from "../../offline/offline";

export function PracticalUpdatesRoute({
  circleId,
  supportNeedId,
  timeZone,
}: {
  readonly circleId: string;
  readonly supportNeedId: string;
  readonly timeZone: string;
}) {
  const result = useQuery(practicalUpdateQueries.forNeed, {
    circleId,
    supportNeedId: practicalUpdateNeedDocumentId(supportNeedId),
  });
  const create = useMutation(practicalUpdateMutations.createSelf);
  const [text, setText] = useState("");
  const [includeInPrint, setIncludeInPrint] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { actionsPaused } = useConnectivity();

  if (result === undefined) return null;

  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = text.trim();
    if (trimmed.length === 0 || trimmed.length > 500) {
      setProblem(COPY.validation);
      return;
    }
    if (actionsPaused) return;

    setProblem(null);
    setBusy(true);
    void create({
      circleId,
      supportNeedId: practicalUpdateNeedDocumentId(supportNeedId),
      text: trimmed,
      includeInPrint,
      notificationRequested: false,
      idempotencyKey: crypto.randomUUID(),
    })
      .then((outcome) => {
        if (outcome.status !== "applied" || !outcome.value.ok) {
          setProblem(COPY.unavailable);
          return;
        }
        setText("");
        setIncludeInPrint(false);
      })
      .catch(() => {
        setProblem(COPY.unavailable);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <section aria-labelledby="practical-updates-heading" className="practical-updates">
      <h2 id="practical-updates-heading">{COPY.heading}</h2>
      <p>{COPY.introduction}</p>

      {result.updates.length === 0 ? (
        <EmptyState title={COPY.empty} />
      ) : (
        <div className="practical-updates__list">
          {result.updates.map((update) => (
            <PracticalUpdateCard
              actionsPaused={actionsPaused}
              canViewHistory={result.canViewHistory}
              circleId={circleId}
              key={update.practicalUpdateId}
              timeZone={timeZone}
              update={update}
            />
          ))}
        </div>
      )}

      {result.canCreateSelf ? (
        <Card>
          <form onSubmit={submit}>
            <h3>{COPY.addHeading}</h3>
            <label>
              <span>{COPY.textLabel}</span>
              <textarea
                aria-describedby="practical-update-hint"
                className={cx.fieldControl}
                maxLength={500}
                onChange={(event) => {
                  setText(event.currentTarget.value);
                }}
                rows={4}
                value={text}
              />
            </label>
            <p id="practical-update-hint">{COPY.textHint}</p>
            <label>
              <input
                checked={includeInPrint}
                onChange={(event) => {
                  setIncludeInPrint(event.currentTarget.checked);
                }}
                type="checkbox"
              />
              <span>
                {COPY.includeInPrint} <small>{COPY.includeInPrintHint}</small>
              </span>
            </label>
            <Button disabled={busy || actionsPaused} type="submit" variant="primary">
              {COPY.add}
            </Button>
          </form>
        </Card>
      ) : null}

      {problem === null ? null : (
        <StatusMessage live="assertive" tone="error">
          {problem}
        </StatusMessage>
      )}
    </section>
  );
}

function PracticalUpdateCard({
  actionsPaused,
  canViewHistory,
  circleId,
  timeZone,
  update,
}: {
  readonly actionsPaused: boolean;
  readonly canViewHistory: boolean;
  readonly circleId: string;
  readonly timeZone: string;
  readonly update: PracticalUpdateView;
}) {
  const updateSelf = useMutation(practicalUpdateMutations.updateSelf);
  const updateProxy = useMutation(practicalUpdateMutations.updateProxy);
  const redactSelf = useMutation(practicalUpdateMutations.redactSelf);
  const redactProxy = useMutation(practicalUpdateMutations.redactProxy);
  const [editing, setEditing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [text, setText] = useState(update.text ?? "");
  const [includeInPrint, setIncludeInPrint] = useState(update.includeInPrint);
  const [pinned, setPinned] = useState(update.pinned);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const person = update.responsibleName ?? COPY.formerMember;
  const recorder = update.recordedByName ?? COPY.formerMember;
  const provenance =
    update.source === "proxy"
      ? COPY.proxyProvenance(person, recorder, update.sourceChannel ?? COPY.formerMember)
      : COPY.recorded(recorder, formatInstant(update.createdAt, timeZone));

  const save = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = text.trim();
    if (trimmed.length === 0 || trimmed.length > 500) {
      setProblem(COPY.validation);
      return;
    }
    if (update.editMode === null || actionsPaused) return;
    setBusy(true);
    setProblem(null);
    const mutate = update.editMode === "self" ? updateSelf : updateProxy;
    void mutate({
      circleId,
      practicalUpdateId: practicalUpdateDocumentId(update.practicalUpdateId),
      expectedRevision: update.revision,
      text: trimmed,
      includeInPrint,
      pinned,
    })
      .then((outcome) => {
        if (outcome.status === "applied" && outcome.value.ok) setEditing(false);
        else setProblem(COPY.unavailable);
      })
      .catch(() => {
        setProblem(COPY.unavailable);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const redact = () => {
    if (update.redactMode === null || actionsPaused) return;
    setBusy(true);
    const mutate = update.redactMode === "self" ? redactSelf : redactProxy;
    void mutate({
      circleId,
      practicalUpdateId: practicalUpdateDocumentId(update.practicalUpdateId),
      expectedRevision: update.revision,
    })
      .then((outcome) => {
        if (outcome.status !== "applied" || !outcome.value.ok) setProblem(COPY.unavailable);
      })
      .catch(() => {
        setProblem(COPY.unavailable);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Card>
      {update.text === null ? <p>{COPY.removed}</p> : <p>{update.text}</p>}
      <p>{provenance}</p>
      {update.pinned ? <p>{COPY.pinned}</p> : null}
      {update.includeInPrint ? <p>{COPY.printIncluded}</p> : null}

      {editing ? (
        <form onSubmit={save}>
          <label>
            <span>{COPY.textLabel}</span>
            <textarea
              className={cx.fieldControl}
              maxLength={500}
              onChange={(event) => {
                setText(event.currentTarget.value);
              }}
              rows={4}
              value={text}
            />
          </label>
          <label>
            <input
              checked={pinned}
              onChange={(event) => {
                setPinned(event.currentTarget.checked);
              }}
              type="checkbox"
            />
            {COPY.pin}
          </label>
          <label>
            <input
              checked={includeInPrint}
              onChange={(event) => {
                setIncludeInPrint(event.currentTarget.checked);
              }}
              type="checkbox"
            />
            {COPY.includeInPrint}
          </label>
          <Button disabled={busy || actionsPaused} type="submit" variant="primary">
            {COPY.save}
          </Button>
          <Button
            disabled={busy}
            onClick={() => {
              setEditing(false);
            }}
          >
            {COPY.cancel}
          </Button>
        </form>
      ) : null}

      {update.editMode === null || editing ? null : (
        <Button
          disabled={busy || actionsPaused}
          onClick={() => {
            setEditing(true);
          }}
        >
          {COPY.edit}
        </Button>
      )}
      {update.redactMode === null ? null : (
        <Button disabled={busy || actionsPaused} onClick={redact} variant="danger">
          {COPY.remove}
        </Button>
      )}
      {canViewHistory ? (
        <Button
          onClick={() => {
            setShowHistory((current) => !current);
          }}
        >
          {COPY.history}
        </Button>
      ) : null}
      {showHistory ? (
        <History
          circleId={circleId}
          practicalUpdateId={update.practicalUpdateId}
          timeZone={timeZone}
        />
      ) : null}
      {problem === null ? null : <StatusMessage tone="error">{problem}</StatusMessage>}
    </Card>
  );
}

function History({
  circleId,
  practicalUpdateId,
  timeZone,
}: {
  readonly circleId: string;
  readonly practicalUpdateId: string;
  readonly timeZone: string;
}) {
  const history = useQuery(practicalUpdateQueries.history, {
    circleId,
    practicalUpdateId: practicalUpdateDocumentId(practicalUpdateId),
  });
  if (history === undefined) return null;
  return (
    <section aria-label={COPY.historyHeading}>
      <h4>{COPY.historyHeading}</h4>
      {history.length === 0 ? <p>{COPY.historyEmpty}</p> : null}
      <ol>
        {history.map((entry) => (
          <li key={entry.historyId}>
            {COPY.historyEntry(
              actionLabel(entry.action),
              entry.actorName ?? COPY.formerMember,
              formatInstant(entry.occurredAt, timeZone),
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

function actionLabel(action: "created" | "updated" | "redacted"): string {
  if (action === "created") return COPY.created;
  if (action === "updated") return COPY.updated;
  return COPY.redacted;
}

function formatInstant(at: number, timeZone: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  }).format(new Date(at));
}
